import { useMemo, useState, type FC } from 'react';
import { useAtomValue } from 'jotai';
import {
  ActionableNotification,
  Button,
  InlineNotification,
  RadioButton,
  Toggle,
} from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { formatNumber } from 'utils/number';
import { formatMutationResource } from 'utils/misc';
import type {
  LandingInterpretationIntervalRow,
  LandingIntervalPickerRequest,
} from './types';
import styles from './select-geologic-interval-drawer.module.scss';

// DataTable requires the row to be an index signature; the catalog row is a fixed shape.
type CatalogRow = LandingInterpretationIntervalRow & Record<string, unknown>;

const SHOW_UNLANDED_LABEL = 'Show geologic intervals with no landings';

interface SelectGeologicIntervalDrawerProps {
  /** What the interpretation reads now, so the drawer opens on it, and whether the bore has a
   *  grid run, without which the Grid Surface column is left off (UAT item 9). */
  request: Pick<
    LandingIntervalPickerRequest,
    'selectedId' | 'hasGridRun' | 'onRetry' | 'intervalsAtom'
  >;
  onClose: () => void;
  onSave: (row: LandingInterpretationIntervalRow) => void;
}

const formatCount = (count: number | null) => {
  if (count === null) return '—';
  return formatNumber(count, 0, 0);
};

const GRID_SURFACE_COLUMN: DataTableColumnDef<CatalogRow> = {
  id: 'gridSurface',
  accessorKey: 'gridSurface',
  header: 'Grid Surface',
  size: 160,
  cell: (info) => info.row.original.gridSurface ?? '—',
};

// Select Geologic Interval (INS-12936): the full basin column behind the Landing Analysis
// Interpretation's catalog card, each interval with its nearby landed-wellbore count. Saving only
// STAGES the pick on the section; Mark as Correct applies it.
// Deliberately not the Change Match drawer: that one re-resolves the pre-drill target zone. This
// attributes the wellbore to an interval, with no scoring, and the two must not share a Save path.
export const SelectGeologicIntervalDrawer: FC<
  SelectGeologicIntervalDrawerProps
> = ({ request, onClose, onSave }) => {
  const { selectedId, hasGridRun, intervalsAtom } = request;
  // The catalog the section fetched for this bore (every interval, landed or not), read live.
  const resource = useAtomValue(intervalsAtom);
  const { loading, data } = formatMutationResource(resource);
  // Review item 13: a failed catalog read says so instead of reading as an empty basin.
  const failed = !!resource.result && resource.result.type !== 'success';
  const rows = useMemo(() => data?.results ?? [], [data]);
  // False when the bore had no surface hole to recall around: no counts, nothing hidden.
  const countsAvailable = data?.nearbyLandingsAvailable ?? true;
  // Staged locally: closing without saving leaves the section's pick alone.
  const [picked, setPicked] = useState<number | null>(selectedId);
  // Intervals nobody landed in are the long tail of the basin column: hidden by default.
  const [showUnlanded, setShowUnlanded] = useState(false);

  const visibleRows = useMemo(() => {
    if (showUnlanded || !countsAvailable) return rows as CatalogRow[];
    // The staged pick always survives the filter, or the drawer would open on nothing selected.
    return rows.filter(
      (row) => (row.nearbyLandedWellCount ?? 0) > 0 || row.id === picked
    ) as CatalogRow[];
  }, [rows, showUnlanded, countsAvailable, picked]);

  const columns = useMemo<DataTableColumnDef<CatalogRow>[]>(
    () => [
      {
        // A visual mirror of the row selection; the row click owns it.
        id: '_radio',
        header: '',
        size: 56,
        minSize: 56,
        enableSorting: false,
        enableHiding: false,
        cell: (info) => (
          <span role="presentation" className={styles.radioCell}>
            <RadioButton
              id={`landing-interval-${info.row.original.id}`}
              name="landing-interval-catalog"
              value={String(info.row.original.id)}
              checked={info.row.getIsSelected()}
              labelText=""
              readOnly
            />
          </span>
        ),
      },
      {
        id: 'geologicInterval',
        accessorKey: 'geologicInterval',
        header: 'Geologic Interval',
        size: 240,
      },
      {
        // UAT item 9: the catalog spans the basin column, so each row says which basin.
        id: 'basinName',
        accessorKey: 'basinName',
        header: 'Basin',
        size: 160,
        cell: (info) => info.row.original.basinName ?? '—',
      },
      {
        id: 'nearbyLandedWellCount',
        accessorKey: 'nearbyLandedWellCount',
        header: 'Nearby Landed Wells in Zone',
        size: 220,
        meta: { type: 'integer' },
        cell: (info) => formatCount(info.row.original.nearbyLandedWellCount),
      },
      // Only with a grid run behind the bore: without one every row reads a dash.
      ...(hasGridRun ? [GRID_SURFACE_COLUMN] : []),
    ],
    [hasGridRun]
  );

  const pickedRow = rows.find((row) => row.id === picked) ?? null;

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Select Geologic Interval</h2>
          <p className={styles.subtitle}>
            Resolve the Wellbore Landing to a Geologic Interval
          </p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        <p className={styles.heading}>Interpret Landing Analysis Results</p>
        <div className={styles.toggleRow}>
          <Toggle
            id="landing-interval-show-unlanded"
            size="sm"
            labelText=""
            labelA=""
            labelB=""
            toggled={showUnlanded || !countsAvailable}
            disabled={!countsAvailable}
            onToggle={setShowUnlanded}
          />
          <label
            htmlFor="landing-interval-show-unlanded"
            className={styles.toggleLabel}
          >
            {SHOW_UNLANDED_LABEL}
          </label>
        </div>
        {/* Review comment 5: reopening re-read nothing, so the notice offers the retry. */}
        {failed && (
          <ActionableNotification
            className={styles.notice}
            kind="error"
            inline
            lowContrast
            hideCloseButton
            hasFocus={false}
            title="Failed to load the geologic intervals."
            subtitle={
              request.onRetry
                ? 'Retry to load them again.'
                : 'Reload the page to try again.'
            }
            actionButtonLabel={request.onRetry ? 'Retry' : undefined}
            onActionButtonClick={request.onRetry}
          />
        )}
        {!countsAvailable && !loading && (
          <InlineNotification
            className={styles.notice}
            kind="info"
            lowContrast
            hideCloseButton
            title="No nearby landings to count."
            subtitle="This wellbore has no surface location to search around, so every geologic interval is listed."
          />
        )}
        <DataTable<CatalogRow>
          data={visibleRows}
          columns={columns}
          getRowId={(row) => String(row.id)}
          isLoading={loading && !data}
          scroll={{ mode: 'paginated' }}
          pageSizes={[10, 25, 50]}
          initialTableState={{
            pageSize: 25,
            selection: {
              selectAll: false,
              toggledNodes: picked === null ? [] : [String(picked)],
            },
          }}
          onSelectionChange={(state) => {
            const key = state.selection.toggledNodes[0];
            setPicked(key === undefined ? null : Number(key));
          }}
          config={{
            selectionType: 'single',
            globalSearch: true,
            columnVisibility: false,
            sorting: true,
            columnFilters: false,
            export: false,
          }}
        />
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" size="lg" onClick={onClose}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="lg"
          disabled={pickedRow === null}
          onClick={() => {
            if (pickedRow) onSave(pickedRow);
          }}
        >
          Select
        </Button>
      </div>
    </div>
  );
};
